/**
 * Copyright 2025 Lumavec Group LLC
 *
 * All rights reserved. No part of this software may be used, copied, modified,
 * or distributed without explicit written permission. This software is provided
 * "as-is" without warranty of any kind, express or implied, including but not
 * limited to warranties of merchantability or fitness for a particular purpose.
 */

import { useState, useCallback, useMemo } from 'react';
import { useQuery, useQueryClient, useMutation } from '@tanstack/react-query';
import type { Agent } from '@shared/schema/agents';
import { listAgents, updateAgentSidebarPreference, updateAgent } from '@shared/lib/api/agents';
import { getUserSettings } from '@shared/lib/api/user_settings';
import { listLLMModels, type LLMModel } from '../../lib/api/system-agents';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Switch } from '@/components/ui/switch';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Textarea } from '@/components/ui/textarea';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { useToast } from '@/hooks/use-toast';
import {
  Bot,
  MessageSquare,
  RefreshCw,
  Loader2,
  Wrench,
  Cpu,
  ChevronRight,
  Eye,
  EyeOff,
  Settings,
  Sparkles,
  LayoutGrid,
  List,
  Edit3,
  Save,
  X,
  Code,
  FileText,
} from 'lucide-react';

type ViewMode = 'grid' | 'list';

/**
 * Renders system prompt template with syntax highlighting for template elements
 * Color scheme:
 * - Required variables {{ var }} / {{ var! }} - blue
 * - Optional variables {{ var? }} - green  
 * - Conditionals {{ %if }}, {{ %else }}, {{ %endif }} - purple
 * - Document includes {{ docs/... }} - orange
 * - Nested/complex {{ var.field }} - cyan
 */
function SyntaxHighlightedPrompt({ content, className = '' }: { content: string; className?: string }) {
  const highlightTemplate = (text: string): React.ReactNode[] => {
    const parts: React.ReactNode[] = [];
    let lastIndex = 0;
    
    // Regex to match template patterns
    const templateRegex = /\{\{([^}]+)\}\}/g;
    let match;
    
    while ((match = templateRegex.exec(text)) !== null) {
      // Add text before the match
      if (match.index > lastIndex) {
        parts.push(
          <span key={`text-${lastIndex}`}>
            {text.slice(lastIndex, match.index)}
          </span>
        );
      }
      
      const fullMatch = match[0];
      const innerContent = match[1].trim();
      
      // Determine the type and color
      let colorClass = '';
      let title = '';
      
      if (innerContent.startsWith('%if') || innerContent === '%else' || innerContent === '%endif') {
        // Conditionals - purple
        colorClass = 'bg-purple-100 text-purple-800 border-purple-300';
        title = 'Conditional block';
      } else if (innerContent.startsWith('docs/') || innerContent.startsWith('prompt/')) {
        // Document/prompt includes - orange
        colorClass = 'bg-orange-100 text-orange-800 border-orange-300';
        title = 'Document/prompt inclusion';
      } else if (innerContent.endsWith('?')) {
        // Optional variables - green
        colorClass = 'bg-green-100 text-green-800 border-green-300';
        title = 'Optional variable (empty if missing)';
      } else if (innerContent.endsWith('!')) {
        // Explicitly required - red
        colorClass = 'bg-red-100 text-red-800 border-red-300';
        title = 'Required variable (fails if missing)';
      } else if (innerContent.includes('.')) {
        // Nested access - cyan
        colorClass = 'bg-cyan-100 text-cyan-800 border-cyan-300';
        title = 'Nested object access';
      } else {
        // Standard required variable - blue
        colorClass = 'bg-blue-100 text-blue-800 border-blue-300';
        title = 'Required variable';
      }
      
      parts.push(
        <span
          key={`template-${match.index}`}
          className={`inline-block px-1 py-0.5 rounded border text-xs font-mono ${colorClass}`}
          title={title}
        >
          {fullMatch}
        </span>
      );
      
      lastIndex = match.index + fullMatch.length;
    }
    
    // Add remaining text
    if (lastIndex < text.length) {
      parts.push(
        <span key={`text-${lastIndex}`}>
          {text.slice(lastIndex)}
        </span>
      );
    }
    
    return parts;
  };

  // Split by lines to preserve formatting
  const lines = content.split('\n');
  
  return (
    <div className={`font-mono text-sm whitespace-pre-wrap ${className}`}>
      {lines.map((line, index) => (
        <div key={index} className="min-h-[1.25rem]">
          {highlightTemplate(line)}
        </div>
      ))}
    </div>
  );
}

interface ChatAgentCardProps {
  agent: Agent;
  isEnabledInSidebar: boolean;
  isUpdating: boolean;
  onToggleSidebar: (agent: Agent, enabled: boolean) => void;
  onClick: (agent: Agent) => void;
}

function ChatAgentCard({ agent, isEnabledInSidebar, isUpdating, onToggleSidebar, onClick }: ChatAgentCardProps) {
  const getCategoryBadgeColor = (category: string) => {
    switch (category) {
      case 'product_management':
        return 'bg-purple-100 text-purple-800 border-purple-200';
      case 'general_assistant':
        return 'bg-blue-100 text-blue-800 border-blue-200';
      case 'curriculum_design':
        return 'bg-green-100 text-green-800 border-green-200';
      case 'tutoring':
        return 'bg-yellow-100 text-yellow-800 border-yellow-200';
      default:
        return 'bg-gray-100 text-gray-800 border-gray-200';
    }
  };

  const formatCategory = (category: string) => {
    return category
      .split('_')
      .map(word => word.charAt(0).toUpperCase() + word.slice(1))
      .join(' ');
  };

  return (
    <Card 
      className={`cursor-pointer transition-all hover:border-primary/50 hover:shadow-md ${
        isEnabledInSidebar ? 'border-primary/30 bg-primary/5' : ''
      }`}
      onClick={() => onClick(agent)}
    >
      <CardContent className="p-5">
        {/* Header */}
        <div className="flex items-start justify-between mb-3">
          <div className="flex items-center gap-3">
            <div className={`p-2 rounded-lg ${isEnabledInSidebar ? 'bg-primary/10' : 'bg-muted'}`}>
              <Bot className={`h-6 w-6 ${isEnabledInSidebar ? 'text-primary' : 'text-muted-foreground'}`} />
            </div>
            <div>
              <h3 className="font-semibold text-base leading-tight">
                {agent.displayName || agent.name}
              </h3>
              <Badge variant="outline" className={`mt-1 text-xs ${getCategoryBadgeColor(agent.category)}`}>
                {formatCategory(agent.category)}
              </Badge>
            </div>
          </div>
          <div 
            className="flex items-center gap-2"
            onClick={(e) => e.stopPropagation()}
          >
            <span className="text-xs text-muted-foreground">
              {isEnabledInSidebar ? 'In sidebar' : 'Hidden'}
            </span>
            {isUpdating ? (
              <Loader2 className="h-4 w-4 animate-spin text-muted-foreground" />
            ) : (
              <Switch
                checked={isEnabledInSidebar}
                onCheckedChange={(checked) => onToggleSidebar(agent, checked)}
                aria-label={`Toggle ${agent.displayName} in sidebar`}
              />
            )}
          </div>
        </div>

        {/* Description */}
        <p className="text-sm text-muted-foreground line-clamp-2 mb-4">
          {agent.description || 'No description available'}
        </p>

        {/* Configuration summary */}
        <div className="flex flex-wrap gap-2 mb-3">
          {/* Model */}
          <div className="flex items-center gap-1.5 text-xs text-muted-foreground bg-muted/50 px-2 py-1 rounded">
            <Cpu className="h-3 w-3" />
            <span>{agent.defaultModel || 'Default model'}</span>
          </div>
          
          {/* Tools count */}
          {agent.enabledTools && agent.enabledTools.length > 0 && (
            <div className="flex items-center gap-1.5 text-xs text-muted-foreground bg-muted/50 px-2 py-1 rounded">
              <Wrench className="h-3 w-3" />
              <span>{agent.enabledTools.length} tools</span>
            </div>
          )}

          {/* Memory status */}
          {agent.memoryEnabled && (
            <div className="flex items-center gap-1.5 text-xs text-green-700 bg-green-50 px-2 py-1 rounded">
              <Sparkles className="h-3 w-3" />
              <span>Memory</span>
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="flex items-center justify-between pt-2 border-t">
          <Badge variant={agent.status === 'active' ? 'default' : 'secondary'} className="text-xs">
            {agent.status === 'active' ? 'Active' : 'Inactive'}
          </Badge>
          <Button variant="ghost" size="sm" className="gap-1 text-xs h-7">
            View Details
            <ChevronRight className="h-3 w-3" />
          </Button>
        </div>
      </CardContent>
    </Card>
  );
}

interface ChatAgentDetailViewProps {
  agent: Agent;
  isEnabledInSidebar: boolean;
  isUpdating: boolean;
  onToggleSidebar: (agent: Agent, enabled: boolean) => void;
  onBack: () => void;
  onAgentUpdated: () => void;
}

function ChatAgentDetailView({ agent, isEnabledInSidebar, isUpdating, onToggleSidebar, onBack, onAgentUpdated }: ChatAgentDetailViewProps) {
  const [isEditingModel, setIsEditingModel] = useState(false);
  const [isEditingPrompt, setIsEditingPrompt] = useState(false);
  const [selectedModel, setSelectedModel] = useState(agent.defaultModel || '');
  const [editedPrompt, setEditedPrompt] = useState(agent.systemPromptTemplate || '');
  const { toast } = useToast();
  const queryClient = useQueryClient();

  // Fetch available LLM models
  const { data: modelsResult, isLoading: isLoadingModels } = useQuery({
    queryKey: ['llmModels'],
    queryFn: () => listLLMModels(),
    staleTime: 5 * 60 * 1000, // 5 minutes
  });

  const availableModels = modelsResult?.models || [];

  // Group models by provider for the dropdown
  const modelsByProvider = useMemo(() => {
    const grouped: Record<string, LLMModel[]> = {};
    availableModels.forEach(model => {
      const provider = model.provider || 'other';
      if (!grouped[provider]) {
        grouped[provider] = [];
      }
      grouped[provider].push(model);
    });
    return grouped;
  }, [availableModels]);

  // Update agent mutation
  const updateMutation = useMutation({
    mutationFn: async (updates: { defaultModel?: string; systemPromptTemplate?: string }) => {
      // Pass allowSystemAgentUpdate to bypass the system agent restriction (Velocity manages agents directly)
      return updateAgent(agent.id, { ...updates, allowSystemAgentUpdate: true });
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['agents'] });
      onAgentUpdated();
    },
    onError: (error: any) => {
      toast({
        title: 'Update failed',
        description: error.message || 'Failed to update agent.',
        variant: 'destructive',
      });
    },
  });

  const handleModelSave = async () => {
    await updateMutation.mutateAsync({ defaultModel: selectedModel });
    setIsEditingModel(false);
    toast({
      title: 'Model updated',
      description: `Default model changed to ${selectedModel}.`,
    });
  };

  const handleModelCancel = () => {
    setSelectedModel(agent.defaultModel || '');
    setIsEditingModel(false);
  };

  const handlePromptSave = async () => {
    await updateMutation.mutateAsync({ systemPromptTemplate: editedPrompt });
    setIsEditingPrompt(false);
    toast({
      title: 'System prompt updated',
      description: 'The system prompt template has been saved.',
    });
  };

  const handlePromptCancel = () => {
    setEditedPrompt(agent.systemPromptTemplate || '');
    setIsEditingPrompt(false);
  };

  const formatCategory = (category: string) => {
    return category
      .split('_')
      .map(word => word.charAt(0).toUpperCase() + word.slice(1))
      .join(' ');
  };

  const formatProviderName = (provider: string) => {
    switch (provider) {
      case 'anthropic': return 'Anthropic';
      case 'openai': return 'OpenAI';
      case 'vertex': return 'Google Vertex AI';
      default: return provider.charAt(0).toUpperCase() + provider.slice(1);
    }
  };

  return (
    <div className="space-y-6">
      {/* Back button */}
      <Button variant="ghost" size="sm" onClick={onBack} className="gap-1.5 -ml-2">
        <ChevronRight className="h-4 w-4 rotate-180" />
        Back to agents
      </Button>

      {/* Header card */}
      <Card>
        <CardHeader>
          <div className="flex items-start justify-between">
            <div className="flex items-center gap-4">
              <div className={`p-3 rounded-xl ${isEnabledInSidebar ? 'bg-primary/10' : 'bg-muted'}`}>
                <Bot className={`h-8 w-8 ${isEnabledInSidebar ? 'text-primary' : 'text-muted-foreground'}`} />
              </div>
              <div>
                <CardTitle className="text-xl">{agent.displayName || agent.name}</CardTitle>
                <CardDescription className="mt-1">{formatCategory(agent.category)}</CardDescription>
              </div>
            </div>
            <div 
              className="flex items-center gap-3 bg-muted/50 px-4 py-2 rounded-lg"
            >
              <div className="flex items-center gap-2">
                {isEnabledInSidebar ? (
                  <Eye className="h-4 w-4 text-primary" />
                ) : (
                  <EyeOff className="h-4 w-4 text-muted-foreground" />
                )}
                <span className="text-sm font-medium">
                  {isEnabledInSidebar ? 'Visible in sidebar' : 'Hidden from sidebar'}
                </span>
              </div>
              {isUpdating ? (
                <Loader2 className="h-4 w-4 animate-spin" />
              ) : (
                <Switch
                  checked={isEnabledInSidebar}
                  onCheckedChange={(checked) => onToggleSidebar(agent, checked)}
                />
              )}
            </div>
          </div>
        </CardHeader>
        <CardContent>
          <p className="text-muted-foreground">{agent.description || 'No description available'}</p>
        </CardContent>
      </Card>

      {/* Configuration details */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        {/* Model Configuration */}
        <Card>
          <CardHeader className="pb-3">
            <div className="flex items-center justify-between">
              <CardTitle className="text-base flex items-center gap-2">
                <Cpu className="h-4 w-4" />
                Model Configuration
              </CardTitle>
              {!isEditingModel ? (
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => setIsEditingModel(true)}
                  className="gap-1.5 h-7"
                >
                  <Edit3 className="h-3 w-3" />
                  Edit
                </Button>
              ) : (
                <div className="flex gap-1">
                  <Button
                    variant="default"
                    size="sm"
                    onClick={handleModelSave}
                    disabled={updateMutation.isPending}
                    className="gap-1 h-7"
                  >
                    {updateMutation.isPending ? (
                      <Loader2 className="h-3 w-3 animate-spin" />
                    ) : (
                      <Save className="h-3 w-3" />
                    )}
                    Save
                  </Button>
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={handleModelCancel}
                    disabled={updateMutation.isPending}
                    className="gap-1 h-7"
                  >
                    <X className="h-3 w-3" />
                    Cancel
                  </Button>
                </div>
              )}
            </div>
          </CardHeader>
          <CardContent className="space-y-3">
            <div className="space-y-2">
              <span className="text-sm text-muted-foreground">Default Model</span>
              {isEditingModel ? (
                <Select value={selectedModel} onValueChange={setSelectedModel}>
                  <SelectTrigger className="w-full">
                    <SelectValue placeholder={isLoadingModels ? "Loading models..." : "Select a model"} />
                  </SelectTrigger>
                  <SelectContent>
                    {isLoadingModels ? (
                      <SelectItem value="_loading" disabled>Loading models...</SelectItem>
                    ) : availableModels.length === 0 ? (
                      <SelectItem value="_none" disabled>No models available</SelectItem>
                    ) : (
                      Object.entries(modelsByProvider).map(([provider, models]) => (
                        <div key={provider}>
                          <div className="px-2 py-1.5 text-xs font-semibold text-muted-foreground bg-muted/50">
                            {formatProviderName(provider)}
                          </div>
                          {models.map((model) => (
                            <SelectItem key={model.id} value={model.id}>
                              {model.name}
                            </SelectItem>
                          ))}
                        </div>
                      ))
                    )}
                  </SelectContent>
                </Select>
              ) : (
                <code className="block text-sm bg-muted px-2 py-1 rounded">
                  {agent.defaultModel || 'Not specified'}
                </code>
              )}
            </div>
            {Boolean(agent.modelConfig) && (
              <>
                {(agent.modelConfig as any).temperature !== undefined && (
                  <div className="flex justify-between items-center">
                    <span className="text-sm text-muted-foreground">Temperature</span>
                    <span className="text-sm font-medium">{(agent.modelConfig as any).temperature}</span>
                  </div>
                )}
                {(agent.modelConfig as any).maxTokens !== undefined && (
                  <div className="flex justify-between items-center">
                    <span className="text-sm text-muted-foreground">Max Tokens</span>
                    <span className="text-sm font-medium">{(agent.modelConfig as any).maxTokens}</span>
                  </div>
                )}
              </>
            )}
            <div className="flex justify-between items-center">
              <span className="text-sm text-muted-foreground">Memory Enabled</span>
              <Badge variant={agent.memoryEnabled ? 'default' : 'secondary'}>
                {agent.memoryEnabled ? 'Yes' : 'No'}
              </Badge>
            </div>
          </CardContent>
        </Card>

        {/* Tools */}
        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="text-base flex items-center gap-2">
              <Wrench className="h-4 w-4" />
              Enabled Tools ({agent.enabledTools?.length || 0})
            </CardTitle>
          </CardHeader>
          <CardContent>
            {agent.enabledTools && agent.enabledTools.length > 0 ? (
              <ScrollArea className="h-32">
                <div className="flex flex-wrap gap-2">
                  {agent.enabledTools.map((tool) => (
                    <Badge key={tool} variant="outline" className="text-xs">
                      {tool}
                    </Badge>
                  ))}
                </div>
              </ScrollArea>
            ) : (
              <p className="text-sm text-muted-foreground">No tools configured</p>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Welcome Message */}
      {agent.welcomeMessage && (
        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="text-base flex items-center gap-2">
              <MessageSquare className="h-4 w-4" />
              Welcome Message
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="bg-muted/50 p-4 rounded-lg">
              <p className="text-sm">{agent.welcomeMessage}</p>
            </div>
          </CardContent>
        </Card>
      )}

      {/* System Prompt Template */}
      <Card>
        <CardHeader className="pb-3">
          <div className="flex items-center justify-between">
            <div>
              <CardTitle className="text-base flex items-center gap-2">
                <Code className="h-4 w-4" />
                System Prompt Template
              </CardTitle>
            </div>
            {!isEditingPrompt ? (
              <Button
                variant="ghost"
                size="sm"
                onClick={() => setIsEditingPrompt(true)}
                className="gap-1.5 h-7"
              >
                <Edit3 className="h-3 w-3" />
                Edit
              </Button>
            ) : (
              <div className="flex gap-1">
                <Button
                  variant="default"
                  size="sm"
                  onClick={handlePromptSave}
                  disabled={updateMutation.isPending}
                  className="gap-1 h-7"
                >
                  {updateMutation.isPending ? (
                    <Loader2 className="h-3 w-3 animate-spin" />
                  ) : (
                    <Save className="h-3 w-3" />
                  )}
                  Save
                </Button>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={handlePromptCancel}
                  disabled={updateMutation.isPending}
                  className="gap-1 h-7"
                >
                  <X className="h-3 w-3" />
                  Cancel
                </Button>
              </div>
            )}
          </div>
        </CardHeader>
        <CardContent>
          {/* Template syntax legend */}
          <div className="flex flex-wrap gap-2 mb-4 p-3 bg-muted/30 rounded-lg border">
            <span className="text-xs text-muted-foreground font-medium">Template syntax:</span>
            <span className="inline-flex items-center gap-1 text-xs">
              <span className="px-1 py-0.5 rounded border bg-blue-100 text-blue-800 border-blue-300 font-mono">{'{{ var }}'}</span>
              <span className="text-muted-foreground">Required</span>
            </span>
            <span className="inline-flex items-center gap-1 text-xs">
              <span className="px-1 py-0.5 rounded border bg-green-100 text-green-800 border-green-300 font-mono">{'{{ var? }}'}</span>
              <span className="text-muted-foreground">Optional</span>
            </span>
            <span className="inline-flex items-center gap-1 text-xs">
              <span className="px-1 py-0.5 rounded border bg-purple-100 text-purple-800 border-purple-300 font-mono">{'{{ %if }}'}</span>
              <span className="text-muted-foreground">Conditional</span>
            </span>
            <span className="inline-flex items-center gap-1 text-xs">
              <span className="px-1 py-0.5 rounded border bg-orange-100 text-orange-800 border-orange-300 font-mono">{'{{ docs/... }}'}</span>
              <span className="text-muted-foreground">Include</span>
            </span>
          </div>
          
          {isEditingPrompt ? (
            <Textarea
              value={editedPrompt}
              onChange={(e) => setEditedPrompt(e.target.value)}
              rows={20}
              className="font-mono text-sm resize-y min-h-[300px]"
              placeholder="Enter the system prompt template..."
            />
          ) : (
            <ScrollArea className="h-[400px] w-full rounded-md border bg-muted/20">
              <div className="p-4">
                {agent.systemPromptTemplate ? (
                  <SyntaxHighlightedPrompt content={agent.systemPromptTemplate} />
                ) : (
                  <p className="text-sm text-muted-foreground italic">No system prompt configured</p>
                )}
              </div>
            </ScrollArea>
          )}
        </CardContent>
      </Card>

      {/* Metadata */}
      <Card>
        <CardHeader className="pb-3">
          <CardTitle className="text-base flex items-center gap-2">
            <FileText className="h-4 w-4" />
            Agent Metadata
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-2">
          <div className="grid grid-cols-2 gap-4 text-sm">
            <div>
              <span className="text-muted-foreground">Internal Name:</span>
              <code className="ml-2 bg-muted px-1.5 py-0.5 rounded">{agent.name}</code>
            </div>
            <div>
              <span className="text-muted-foreground">Status:</span>
              <Badge className="ml-2" variant={agent.status === 'active' ? 'default' : 'secondary'}>
                {agent.status}
              </Badge>
            </div>
            <div>
              <span className="text-muted-foreground">System Agent:</span>
              <span className="ml-2">{agent.isSystem ? 'Yes' : 'No'}</span>
            </div>
            <div>
              <span className="text-muted-foreground">Public:</span>
              <span className="ml-2">{agent.isPublic ? 'Yes' : 'No'}</span>
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}

export function ChatAgentsTab() {
  const [viewMode, setViewMode] = useState<ViewMode>('grid');
  const [selectedAgent, setSelectedAgent] = useState<Agent | null>(null);
  const [updatingAgentIds, setUpdatingAgentIds] = useState<Set<number>>(new Set());
  const { toast } = useToast();
  const queryClient = useQueryClient();

  // Fetch all agents
  const { data: agentsData, isLoading: isLoadingAgents, error: agentsError, refetch: refetchAgents } = useQuery({
    queryKey: ['agents'],
    queryFn: () => listAgents(),
  });

  // Fetch user settings for sidebar preferences
  const { data: userSettings, isLoading: isLoadingSettings, refetch: refetchSettings } = useQuery(getUserSettings);

  // Get sidebar visibility map from user settings
  const sidebarVisibility = useMemo(() => {
    const agentSettings = (userSettings?.agentSettings as any) || {};
    return agentSettings.sidebarVisibility || {};
  }, [userSettings]);

  // Filter and sort agents - show all agents, not just those enabled in sidebar
  // This allows users to enable agents they want to see in the sidebar
  const allAgents = useMemo(() => {
    if (!agentsData?.agents) return [];
    
    // Sort by: enabled in sidebar first, then by displayName
    return [...agentsData.agents].sort((a, b) => {
      const aEnabled = sidebarVisibility.hasOwnProperty(String(a.id)) 
        ? sidebarVisibility[String(a.id)] 
        : (a.isEnabledInSidebar ?? false);
      const bEnabled = sidebarVisibility.hasOwnProperty(String(b.id)) 
        ? sidebarVisibility[String(b.id)] 
        : (b.isEnabledInSidebar ?? false);
      
      if (aEnabled && !bEnabled) return -1;
      if (!aEnabled && bEnabled) return 1;
      return (a.displayName || a.name).localeCompare(b.displayName || b.name);
    });
  }, [agentsData?.agents, sidebarVisibility]);

  // Count enabled agents
  const enabledCount = useMemo(() => {
    return allAgents.filter(agent => {
      const agentIdKey = String(agent.id);
      return sidebarVisibility.hasOwnProperty(agentIdKey) 
        ? sidebarVisibility[agentIdKey] 
        : (agent.isEnabledInSidebar ?? false);
    }).length;
  }, [allAgents, sidebarVisibility]);

  // Check if an agent is enabled in sidebar
  const isAgentEnabledInSidebar = useCallback((agent: Agent): boolean => {
    const agentIdKey = String(agent.id);
    return sidebarVisibility.hasOwnProperty(agentIdKey) 
      ? sidebarVisibility[agentIdKey] 
      : (agent.isEnabledInSidebar ?? false);
  }, [sidebarVisibility]);

  // Handle toggling sidebar visibility
  const handleToggleSidebar = useCallback(async (agent: Agent, enabled: boolean) => {
    setUpdatingAgentIds(prev => new Set(prev).add(agent.id));
    
    try {
      await updateAgentSidebarPreference(agent.id, enabled);
      
      // Invalidate queries to refresh data
      await Promise.all([
        queryClient.invalidateQueries({ queryKey: ['userSettings'] }),
        queryClient.invalidateQueries({ queryKey: ['agents'] }),
      ]);
      
      toast({
        title: enabled ? 'Agent added to sidebar' : 'Agent removed from sidebar',
        description: `${agent.displayName || agent.name} is now ${enabled ? 'visible' : 'hidden'} in the sidebar.`,
      });
    } catch (error: any) {
      toast({
        title: 'Failed to update preference',
        description: error.message || 'An error occurred while updating the agent preference.',
        variant: 'destructive',
      });
    } finally {
      setUpdatingAgentIds(prev => {
        const next = new Set(prev);
        next.delete(agent.id);
        return next;
      });
    }
  }, [queryClient, toast]);

  // Handle refresh
  const handleRefresh = useCallback(async () => {
    await Promise.all([refetchAgents(), refetchSettings()]);
  }, [refetchAgents, refetchSettings]);

  // Handle when an agent is updated - refresh the selected agent from the query cache
  const handleAgentUpdated = useCallback(() => {
    if (selectedAgent && agentsData?.agents) {
      const updatedAgent = agentsData.agents.find(a => a.id === selectedAgent.id);
      if (updatedAgent) {
        setSelectedAgent(updatedAgent);
      }
    }
  }, [selectedAgent, agentsData?.agents]);

  // If an agent is selected, show the detail view
  if (selectedAgent) {
    // Get the latest version of the selected agent from the query cache
    const currentAgent = agentsData?.agents?.find(a => a.id === selectedAgent.id) || selectedAgent;
    
    return (
      <ChatAgentDetailView
        agent={currentAgent}
        isEnabledInSidebar={isAgentEnabledInSidebar(currentAgent)}
        isUpdating={updatingAgentIds.has(currentAgent.id)}
        onToggleSidebar={handleToggleSidebar}
        onBack={() => setSelectedAgent(null)}
        onAgentUpdated={handleAgentUpdated}
      />
    );
  }

  const isLoading = isLoadingAgents || isLoadingSettings;

  return (
    <>
      {/* Info Banner */}
      <Card className="mb-6 border-blue-200 bg-blue-50">
        <CardContent className="py-4">
          <div className="flex items-center gap-3">
            <MessageSquare className="h-5 w-5 text-blue-600" />
            <div>
              <p className="font-medium text-blue-900">Chat Agents Configuration</p>
              <p className="text-sm text-blue-700">
                Configure which AI agents appear in the sidebar chat. Toggle visibility to customize your workflow.
                Currently showing {enabledCount} agent{enabledCount !== 1 ? 's' : ''} in the sidebar.
              </p>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Controls */}
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center gap-4">
          <span className="text-sm text-muted-foreground">
            {allAgents.length} agent{allAgents.length !== 1 ? 's' : ''} available
          </span>
          <Badge variant="secondary" className="gap-1">
            <Eye className="h-3 w-3" />
            {enabledCount} in sidebar
          </Badge>
        </div>
        <div className="flex items-center gap-2">
          <Button
            variant={viewMode === 'grid' ? 'secondary' : 'ghost'}
            size="icon"
            onClick={() => setViewMode('grid')}
          >
            <LayoutGrid className="h-4 w-4" />
          </Button>
          <Button
            variant={viewMode === 'list' ? 'secondary' : 'ghost'}
            size="icon"
            onClick={() => setViewMode('list')}
          >
            <List className="h-4 w-4" />
          </Button>
          <Button variant="outline" size="sm" onClick={handleRefresh} disabled={isLoading} className="ml-2">
            <RefreshCw className={`h-4 w-4 mr-2 ${isLoading ? 'animate-spin' : ''}`} />
            Refresh
          </Button>
        </div>
      </div>

      {/* Loading State */}
      {isLoading ? (
        <div className="flex items-center justify-center py-16">
          <div className="text-center">
            <Loader2 className="h-10 w-10 animate-spin text-muted-foreground mx-auto mb-4" />
            <p className="text-sm text-muted-foreground">Loading agents...</p>
          </div>
        </div>
      ) : agentsError ? (
        <div className="flex items-center justify-center py-16">
          <div className="text-center">
            <Bot className="h-16 w-16 text-red-300 mx-auto mb-4" />
            <h3 className="text-lg font-medium text-red-600 mb-2">Failed to Load Agents</h3>
            <p className="text-sm text-muted-foreground mb-4">
              {(agentsError as Error).message || 'An error occurred while loading agents.'}
            </p>
            <Button variant="outline" onClick={handleRefresh}>
              Try Again
            </Button>
          </div>
        </div>
      ) : allAgents.length === 0 ? (
        <div className="flex items-center justify-center py-16">
          <div className="text-center">
            <Bot className="h-16 w-16 text-muted-foreground/50 mx-auto mb-4" />
            <h3 className="text-lg font-medium mb-2">No Agents Available</h3>
            <p className="text-sm text-muted-foreground mb-4 max-w-md">
              No AI agents are configured. Agents need to be set up by an administrator.
            </p>
          </div>
        </div>
      ) : (
        /* Agent Grid/List */
        <div className={
          viewMode === 'grid'
            ? 'grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4'
            : 'space-y-4'
        }>
          {allAgents.map((agent) => (
            <ChatAgentCard
              key={agent.id}
              agent={agent}
              isEnabledInSidebar={isAgentEnabledInSidebar(agent)}
              isUpdating={updatingAgentIds.has(agent.id)}
              onToggleSidebar={handleToggleSidebar}
              onClick={setSelectedAgent}
            />
          ))}
        </div>
      )}
    </>
  );
}
